/**
 * @description Parses a string or number value into a standardized numeric currency format,
 * handling various international currency input conventions. This function intelligently
 * determines decimal and thousand separators based on their position and context,
 * identifies negative values, and truncates the resulting numeric value to exactly two
 * decimal places.
 *
 * @param value The input value, which can be a string (e.g., "1.234,56", "1,234.56", "123456", "-500", "R$ 1.234,56")
 *              or a number (e.g., 1234.56, -500).
 * @returns A number representing the parsed currency value (e.g., 1234.56, -500.00),
 *          always with at most two decimal places due to truncation, or `null` if the
 *          input cannot be successfully parsed into a valid number.
 */
export function parseCurrencyValue(value: string | number): number | null {
  /**
   * Handles null or undefined input values.
   * If the input is explicitly `null` or `undefined`, it cannot be processed as a currency value.
   * In such cases, the function immediately returns `null`, indicating an invalid or unparsable input.
   */
  if (value === null || value === undefined) {
    return null;
  }

  /**
   * Converts the input value to a string and initializes a negative flag.
   * Regardless of whether the initial input was a number or a string, it's converted
   * to a string representation to uniformly apply parsing logic.
   * The `isNegative` flag is used to track if the original number was negative, allowing
   * the parsing logic to operate on the absolute value, with the negative sign reapplied at the end.
   */
  let stringValue = String(value).trim(); // Trim leading/trailing spaces
  let isNegative = false;

  if (stringValue.startsWith('-')) {
    isNegative = true;
    stringValue = stringValue.substring(1);
  }

  // Removes all characters that are not digits, commas, or dots from the string.
  stringValue = stringValue.replace(/[^0-9.,]/g, '');

  // Handles cases where the string becomes empty or contains only separators after cleaning.
  if (stringValue.length === 0 || stringValue === '.' || stringValue === ',') {
    return null;
  }

  const lastCommaIndex = stringValue.lastIndexOf(',');
  const lastDotIndex = stringValue.lastIndexOf('.');

  let processedString = '';

  // Determines the effective decimal separator and cleans the string for `Number()` conversion.
  // This logic differentiates between thousand and decimal separators based on their position
  // and the number of digits following them.
  if (lastCommaIndex > -1 && lastDotIndex > -1) {
    // Both comma and dot exist. The one appearing last is considered the decimal separator.
    if (lastCommaIndex > lastDotIndex) {
      // Example: "1.234.567,89" - comma is decimal, dots are thousand separators.
      // Remove all dots, then replace the comma with a dot to standardize.
      processedString = stringValue.replace(/\./g, '').replace(/,/g, '.');
    } else {
      // Example: "1,234,567.89" - dot is decimal, commas are thousand separators.
      // Remove all commas to standardize.
      processedString = stringValue.replace(/,/g, '');
    }
  } else if (lastCommaIndex > -1) {
    // Only comma exists.
    // If the part after the comma has 1 or 2 digits, it's treated as a decimal separator.
    // Otherwise, it's treated as a thousand separator.
    const afterComma = stringValue.substring(lastCommaIndex + 1);
    if (afterComma.length <= 2) {
      // Example: "123,45" - comma is decimal. Replace comma with dot.
      processedString = stringValue.replace(/,/g, '.');
    } else {
      // Example: "6,149" - comma is thousand. Remove comma.
      processedString = stringValue.replace(/,/g, '');
    }
  } else if (lastDotIndex > -1) {
    // Only dot exists.
    // If the part after the dot has 1 or 2 digits, it's treated as a decimal separator.
    // Otherwise, it's treated as a thousand separator.
    const afterDot = stringValue.substring(lastDotIndex + 1);
    if (afterDot.length <= 2) {
      // Example: "123.45" - dot is decimal. No change needed as dot is standard.
      processedString = stringValue;
    } else {
      // Example: "6.149" - dot is thousand. Remove dot.
      processedString = stringValue.replace(/\./g, '');
    }
  } else {
    // No comma or dot separators found. The string is treated as a whole number.
    processedString = stringValue;
  }

  // Handles cases with multiple potential decimal points (e.g., "1.2.3").
  // Only the first dot is considered a true decimal separator. Any subsequent dots
  // are treated as part of the decimal value (e.g., "1.2.3" becomes "1.23").
  const parts = processedString.split('.');
  let finalString = parts[0]; // The integer part is always the first segment.

  if (parts.length > 1) {
    // If there are more parts, concatenate them after the first part with a single dot.
    // This effectively flattens "1.2.3" into "1.23".
    finalString += '.' + parts.slice(1).join('');
  }

  // Truncates the decimal part of the `finalString` to exactly two digits.
  // If the string does not contain a decimal point, no truncation occurs.
  if (finalString.includes('.')) {
    const [integerPart, decimalPart] = finalString.split('.');
    finalString = `${integerPart}.${decimalPart.substring(0, 2)}`;
  }

  let parsedValue = Number(finalString);

  if (isNaN(parsedValue)) {
    return null;
  }

  if (isNegative) {
    parsedValue = -parsedValue;
  }

  return parsedValue;
}
